"use client";

import React, { useState } from "react";
import { Activity, ChartBar, Calculator, CircleCheck, ChevronRight, CreditCard, LayoutDashboard, ChartPie, TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  sales: 18600,
  profit: 8000
}, {
  month: "Fev",
  sales: 22500,
  profit: 11200
}, {
  month: "Mar",
  sales: 28000,
  profit: 14500
}, {
  month: "Abr",
  sales: 26000,
  profit: 13000
}, {
  month: "Mai",
  sales: 32000,
  profit: 17800
}, {
  month: "Jun",
  sales: 38500,
  profit: 21000
}];
const chartConfig = {
  sales: {
    label: "Vendas",
    color: "var(--chart-1)"
  },
  profit: {
    label: "Lucro Líquido",
    color: "var(--chart-2)"
  }
} satisfies ChartConfig;
export default function PortalDeGestaoInteligente() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [isPopoverOpen1, setIsPopoverOpen1] = useState(false);
  const [isPopoverOpen2, setIsPopoverOpen2] = useState(false);
  return <section data-section-id="4189" id="por-que-escolher-o-microvix" className="relative w-full py-24 overflow-hidden bg-background" ref={ref}>
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --fume-bg: hsla(0, 0%, 15%, 0.7);
        }
        .dark, :root {
          --background: var(--fume-bg);
        }
        [data-section-id="4189"],
        [data-section-id="4189"] .card,
        [data-section-id="4189"] .popover,
        [data-section-id="4189"] .modal {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px);
        }
        [data-section-id="4189"] .bg-card,
        [data-section-id="4189"] .bg-popover,
        [data-section-id="4189"] .bg-modal,
        [data-section-id="4189"] .bg-section {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px);
        }
        [data-section-id="4189"] .card *,
        [data-section-id="4189"] .popover *,
        [data-section-id="4189"] .modal * {
          color: var(--foreground);
        }
      `
    }} />
      <div className="absolute top-0 right-0 -z-10 w-1/2 h-full bg-gradient-to-b from-primary/5 to-transparent opacity-50 blur-3xl" />
      <div className="absolute bottom-0 left-0 -z-10 w-1/3 h-2/3 bg-[#FF006E]/5 rounded-full blur-3xl" />

      <div className={`container mx-auto px-4 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          
          {}
          <div className="lg:col-span-5 flex flex-col gap-8 z-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF006E]/10 text-[#FF006E] text-sm font-semibold w-fit border border-[#FF006E]/20">
                <LayoutDashboard className="w-4 h-4" />
                <span>ERP Cloud Especialista</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
                Por que escolher o <span className="text-[#FF006E]">Microvix?</span>
              </h2>
              <p className="text-lg text-foreground/80 leading-relaxed">
                O ERP líder para varejo e franquias. Tenha uma visão 360º da sua operação, desde o estoque centralizado até a frente de caixa omnichannel.
              </p>
            </div>

            <div className="space-y-4">
              {["Gestão multi-loja e franquias unificada", "Controle de estoque e giro em tempo real", "Faturamento fiscal e contábil integrado", "Dashboards de performance e BI nativo"].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF006E]/10 flex items-center justify-center text-[#FF006E]">
                    <CircleCheck className="w-4 h-4" />
                  </div>
                  <span className="text-foreground font-medium">{item}</span>
                </div>)}
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button size="lg" className="rounded-[10px] px-8 bg-[#FF006E] hover:bg-[#FF006E]/90 shadow-lg shadow-[#FF006E]/20" asChild>
                <Link to="/ContactsPage">
                  Solicitar Demonstração <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="rounded-[10px] px-8 border-[#FF006E]/20 text-foreground hover:bg-[#FF006E]/5" asChild>
                <Link to="/ErpMicrovixFunctionalitiesPage">
                  Ver Funcionalidades
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 relative lg:-ml-12 mt-12 lg:mt-0">
            {}
            <Card className="w-full shadow-xl border-border/50 bg-card overflow-hidden relative z-0 rounded-[10px]">
              <CardHeader className="border-b border-border/40 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2 text-foreground">
                      <Activity className="w-5 h-5 text-[#FF006E]" />
                      Painel de Performance
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">Visão consolidada da rede</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 rounded-full bg-muted/20 flex items-center justify-center">
                      <ChartBar className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="h-8 w-8 rounded-full bg-[#FF006E]/10 flex items-center justify-center">
                      <ChartPie className="w-4 h-4 text-[#FF006E]" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 min-h-[300px]">
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <AreaChart data={chartData} margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF006E" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF006E" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#25D366" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#25D366" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => value.slice(0, 3)} tick={{
                    fill: 'var(--muted-foreground)'
                  }} />
                    <YAxis tickLine={false} axisLine={false} tickFormatter={value => `R$${value / 1000}k`} width={60} tick={{
                    fill: 'var(--muted-foreground)'
                  }} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
                    <Area dataKey="profit" type="natural" fill="url(#fillProfit)" fillOpacity={0.4} stroke="#25D366" strokeWidth={2} stackId="a" />
                    <Area dataKey="sales" type="natural" fill="url(#fillSales)" fillOpacity={0.4} stroke="#FF006E" strokeWidth={2} stackId="a" />
                  </AreaChart>
                </ChartContainer>
              </CardContent>

              {}
              <div className="absolute top-[30%] right-[15%] z-20">
                <Popover open={isPopoverOpen1} onOpenChange={setIsPopoverOpen1}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative group w-4 h-4 p-0 hover:bg-transparent" onMouseEnter={() => setIsPopoverOpen1(true)} onMouseLeave={() => setIsPopoverOpen1(false)}>
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF006E] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-4 w-4 bg-[#FF006E] border-2 border-white shadow-sm"></span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="center" className="w-64 p-0 overflow-hidden shadow-xl border-[#FF006E]/20 rounded-[10px] bg-popover">
                    <div className="bg-[#FF006E]/5 p-3 border-b border-[#FF006E]/10 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#FF006E]" />
                      <span className="font-semibold text-sm text-foreground">Monitoramento Real-Time</span>
                    </div>
                    <div className="p-3 bg-popover">
                      <p className="text-xs text-muted-foreground mb-2">
                        Acompanhe o desempenho de cada unidade da sua franquia instantaneamente.
                      </p>
                      <div className="flex items-center justify-between text-xs font-medium bg-muted/20 p-2 rounded">
                        <span className="text-foreground">Ticket Médio:</span>
                        <span className="text-[#25D366]">+ 12.5%</span>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {}
              <div className="absolute bottom-[20%] left-[10%] z-20">
                <Popover open={isPopoverOpen2} onOpenChange={setIsPopoverOpen2}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon" className="relative group w-10 h-10 rounded-full shadow-lg border-border/50 hover:scale-110 transition-transform bg-background" onMouseEnter={() => setIsPopoverOpen2(true)} onMouseLeave={() => setIsPopoverOpen2(false)}>
                      <Calculator className="w-5 h-5 text-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent side="right" className="w-72 shadow-xl border-border/50 rounded-[10px] bg-popover">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-foreground leading-none flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-[#FF006E]" /> Simulador de ROI
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Calcule a economia operacional ao migrar sua gestão para a nuvem Microvix.
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="bg-muted/20 p-2 rounded text-center">
                          <span className="block text-[10px] text-muted-foreground uppercase">Custo Atual</span>
                          <span className="text-sm font-bold text-foreground">Alto</span>
                        </div>
                        <div className="bg-[#FF006E]/5 p-2 rounded text-center border border-[#FF006E]/10">
                          <span className="block text-[10px] text-[#FF006E] uppercase">Com Microvix</span>
                          <span className="text-sm font-bold text-[#FF006E]">Otimizado</span>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </Card>

            <Card className="absolute -bottom-6 -right-6 w-48 shadow-xl border-border/50 bg-card backdrop-blur animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300 hidden md:block rounded-[10px]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#FF006E]/10 flex items-center justify-center text-[#FF006E]">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Performance</p>
                  <p className="text-sm font-bold text-foreground">99.9% Uptime</p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </section>;
}